import React, {useState, useEffect, useMemo, useRef} from 'react'
import {observer} from 'mobx-react'
import {useBasketStore, usePreBookStore, useRapidDeliveryStore} from '../../../../store/hooks/useStore'
import {Box} from '../../../../vanilla'
import {BookingConfirmationModal} from '../booking-confirmation-modal'
import {BookingSlotErrorModal} from '../booking-slot-error-modal'
import {ChangingSlotWarningModal} from '../changing-slot-warning-modal'
import useDeliveryBooking from '../../../../hooks/use-delivery-booking'
import {TimeSlot} from '../../../../types/store/basket'
import {DeliveryDateSlot} from './delivery-date-slot'
import {useSlotReservation} from '../../../../store/hooks/useSlotReservation'
import {isPrebookExpired, isPrebookOnDay, slotsMatch} from '../../../../utils/booking-utils'
import {useViewShippingDetailsTracking, useAddShippingDetailsTracking} from '../../../../analytics/shipping-tracking'
interface TimeSlotsProps {
  selectedTab?: number
  smallBasketEnabled?: boolean
  onRequestDeliveryTypeChange?: (onConfirm: () => void) => void
}

const TimeSlotsComponent = ({selectedTab, smallBasketEnabled, onRequestDeliveryTypeChange}: TimeSlotsProps) => {
  const {currentDeliverySlotId, deliverySlotArray, getDeliverySlots} = useBasketStore()
  const {isRapidDeliveryBooked} = useRapidDeliveryStore()
  const {prebookedOrder} = usePreBookStore()
  const {reserveSlot} = useDeliveryBooking()
  const sendViewShippingDetailsData = useViewShippingDetailsTracking()
  const addShippingDetailsData = useAddShippingDetailsTracking()
  const lastTrackedTabRef = useRef<number | undefined>(undefined)
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | undefined>()
  const deliveryDay = deliverySlotArray[typeof selectedTab === 'number' ? selectedTab : 1]

  const {
    customerHasBookedSlot,
    bookSlotErrorCode,
    handleSlotReservation,
    handleSlotRebook,
    handleShippingAddressValidation,
    errorBookingSlotModal,
    confirmationModal,
    changeSlotWarningModal
  } = useSlotReservation()

  // Merge prebook slot into the slots list if valid and on this day
  const processedSlots = useMemo(() => {
    if (!deliveryDay?.slots) return []

    // Check if prebook is valid (not expired) and falls on this day
    const prebookValid =
      prebookedOrder &&
      !isPrebookExpired(prebookedOrder.c_slotExpiryTime) &&
      isPrebookOnDay(prebookedOrder, deliveryDay)

    if (!prebookValid) {
      return deliveryDay.slots
    }

    // Find the matching slot for this prebook's time window
    const matchingSlotIndex = deliveryDay.slots.findIndex((slot) =>
      slotsMatch(slot.id, prebookedOrder.c_deliverySlotId),
    )

    if (matchingSlotIndex === -1) {
      return deliveryDay.slots
    }

    // Override only the properties needed for prebook activation,
    // keeping all original slot data (restrictedProductClasses, shippingCost, etc.)
    const newSlots = [...deliveryDay.slots]
    newSlots[matchingSlotIndex] = {
      ...deliveryDay.slots[matchingSlotIndex],
      available: true,
      isPrebookSlot: true,
      prebookSlotId: prebookedOrder.c_deliverySlotId,
    }
    return newSlots
  }, [deliveryDay, prebookedOrder])

  const handleSlotBooking = async (slot: TimeSlot) => {
    setSelectedSlot({
      ...slot,
    })

    if (isRapidDeliveryBooked && onRequestDeliveryTypeChange) {
      onRequestDeliveryTypeChange(async () => {
        if (customerHasBookedSlot && slot.id !== currentDeliverySlotId) {
          await handleSlotRebook(slot.id, true, true)
        } else {
          await handleSlotReservation(slot)
        }
      })
      return
    }

    if (customerHasBookedSlot && slot.id !== currentDeliverySlotId) {
      await handleSlotRebook(slot.id, true)
    } else {
      await handleSlotReservation(slot)
    }
  }

  useEffect(() => {
    if (errorBookingSlotModal.isOpen) {
      // If the slot error message is displayed, we need to refresh the slots
      getDeliverySlots()
    }
  }, [errorBookingSlotModal.isOpen])

  useEffect(() => {
    if (!deliveryDay || processedSlots.length === 0) {
      return
    }

    if (lastTrackedTabRef.current === selectedTab) {
      return
    }

    lastTrackedTabRef.current = selectedTab

    sendViewShippingDetailsData({
      deliveryDay,
      slots: processedSlots,
      currentDeliverySlotId
    })
  }, [selectedTab, deliveryDay, processedSlots, sendViewShippingDetailsData])

  return (
    <Box>
      {processedSlots.map((slot, idx) => (
        <DeliveryDateSlot
          smallBasketEnabled={smallBasketEnabled}
          key={slot.id}
          slot={slot}
          vip={slot.vipSlot || false}
          deliveryDay={deliveryDay}
          onReservation={handleSlotBooking}
          isModal={false}
          style={{marginBottom: idx === processedSlots.length - 1 ? '0px' : '1px'}}
          prebook={slot.isPrebookSlot || false}
        />
      ))}

      <BookingConfirmationModal
        {...confirmationModal}
        slot={selectedSlot}
        deliveryDay={deliveryDay}
        smallBasketEnabled={smallBasketEnabled}
      />

      <ChangingSlotWarningModal
        {...changeSlotWarningModal}
        newSlot={selectedSlot ?? null}
        onSubmit={async () => {
          if (selectedSlot) {
            try {
              if (handleShippingAddressValidation()) {
                await reserveSlot(selectedSlot.id)
                addShippingDetailsData({
                  deliveryDay: {
                    isRapid: false,
                  },
                  slot: selectedSlot,
                  currentDeliverySlotId: selectedSlot.id
                })
                confirmationModal.onOpen()
              } else {
                errorBookingSlotModal.onOpen()
              }
            } catch (error) {
              errorBookingSlotModal.onOpen()
            }
          }
        }}
      />

      <BookingSlotErrorModal
        {...errorBookingSlotModal}
        statusCode={bookSlotErrorCode}
        isValidShippingAddress={handleShippingAddressValidation()}
      />
    </Box>
  )
}
export const TimeSlots = observer(TimeSlotsComponent)
